import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Phone, Menu, X, Star, Clock, CheckCircle, Flame, Snowflake, Wind, MapPin, Mail, Calendar } from 'lucide-react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Label } from './components/ui/label';
import './App.css';

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const services = [
    {
      icon: Flame,
      title: 'Heating Services',
      description: 'Expert furnace repairs, replacements, and maintenance to keep you warm all winter long.'
    },
    {
      icon: Snowflake,
      title: 'Cooling Services',
      description: 'Professional AC installation, repair, and tune-ups for maximum comfort during hot summer days.'
    },
    {
      icon: Wind,
      title: 'Indoor Air Quality',
      description: 'Advanced filtration systems, duct cleaning, and humidity control for healthier indoor air.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      location: 'Beaverton, OR',
      rating: 5,
      text: 'Called them at 7 AM when our furnace died overnight. They had a technician here within an hour! Professional, honest pricing, and got us warm again quickly.'
    },
    {
      name: 'David Chen',
      location: 'Beaverton, OR',
      rating: 5,
      text: 'Finally found an HVAC company I can trust. No upselling, just honest recommendations and quality work. Their weekend availability is a lifesaver!'
    },
    {
      name: 'Jennifer Rodriguez',
      location: 'Beaverton, OR',
      rating: 5,
      text: 'Exceptional service! They installed our new AC system and the difference is incredible. Fair pricing and the team was respectful of our home.'
    }
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const submitButton = e.target.querySelector('button[type="submit"]');
    const resultDiv = document.getElementById('contact-result');
    
    submitButton.disabled = true;
    submitButton.textContent = 'Sending...';
    
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        resultDiv.textContent = 'Thank you! We\'ll contact you within 24 hours.';
        resultDiv.className = 'mt-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm';
        e.target.reset();
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      resultDiv.textContent = 'Something went wrong. Please call us directly at (971) 366-5948.';
      resultDiv.className = 'mt-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm';
    } finally {
      submitButton.disabled = false;
      submitButton.textContent = 'Send Message';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/70 backdrop-blur-xl border-b border-slate-200/50 backdrop-saturate-150 shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/6de07f33-8792-4205-9d68-0162561d985b/images/fb9d16bc83522b5dabea48add564fa81a7742b5e12468b7fae67c337b24374b6.png" 
                alt="Beaverton Heating and Cooling Logo" 
                className="h-12 w-12"
              />
              <div>
                <h1 className="text-lg font-bold text-foreground font-chivo">Beaverton Heating & Cooling</h1>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-3 w-3 fill-secondary text-secondary" />
                  ))}
                  <span className="text-xs text-muted-foreground ml-1">5.0</span>
                </div>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <a href="#services" className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                Services
              </a>
              <a href="#about" className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                About
              </a>
              <a href="#testimonials" className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                Reviews
              </a>
              <a href="#contact" className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                Contact
              </a>
              <a href="tel:+19713665948" data-testid="header-call-button">
                <Button className="bg-secondary hover:bg-secondary/90 text-white font-semibold rounded-full animate-pulse-ring">
                  <Phone className="h-4 w-4 mr-2" />
                  (971) 366-5948
                </Button>
              </a>
            </div>

            <button
              className="md:hidden text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-slate-200"
          >
            <div className="container mx-auto px-6 py-4 flex flex-col gap-4">
              <a href="#services" className="text-sm font-medium text-foreground" onClick={() => setMobileMenuOpen(false)}>
                Services
              </a>
              <a href="#about" className="text-sm font-medium text-foreground" onClick={() => setMobileMenuOpen(false)}>
                About
              </a>
              <a href="#testimonials" className="text-sm font-medium text-foreground" onClick={() => setMobileMenuOpen(false)}>
                Reviews
              </a>
              <a href="#contact" className="text-sm font-medium text-foreground" onClick={() => setMobileMenuOpen(false)}>
                Contact
              </a>
              <a href="tel:+19713665948" data-testid="mobile-call-button">
                <Button className="w-full bg-secondary hover:bg-secondary/90 text-white font-semibold rounded-full">
                  <Phone className="h-4 w-4 mr-2" />
                  Call Now
                </Button>
              </a>
            </div>
          </motion.div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6 overflow-hidden">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-16 lg:gap-20 items-center">
            <motion.div
              className="md:col-span-7"
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-secondary/10 text-secondary px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Star className="h-4 w-4 fill-secondary" />
                5-Star Rated HVAC Experts
              </motion.div>
              
              <motion.h1
                variants={fadeInUp}
                className="text-4xl sm:text-5xl lg:text-6xl font-black text-foreground tracking-tighter mb-6 font-chivo"
              >
                Beaverton's 5-Star <span className="text-primary">Heating & Cooling</span> Experts
              </motion.h1>
              
              <motion.p
                variants={fadeInUp}
                className="text-base sm:text-lg text-muted-foreground mb-8 max-w-2xl"
              >
                Fast, reliable, and honest HVAC services when you need them most. From emergency furnace repairs to new AC installations, our highly-rated team is available 7 days a week to keep your home comfortable.
              </motion.p>
              
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+19713665948" data-testid="hero-call-button">
                  <Button className="bg-secondary hover:bg-secondary/90 text-white text-lg font-semibold py-6 px-8 rounded-lg hover:translate-y-[-2px] hover:shadow-lg transition-all">
                    <Phone className="h-5 w-5 mr-2" />
                    Call (971) 366-5948
                  </Button>
                </a>
                <a href="#contact" data-testid="hero-emergency-button">
                  <Button variant="outline" className="border-2 border-primary text-primary hover:bg-primary hover:text-white text-lg font-semibold py-6 px-8 rounded-lg hover:translate-y-[-2px] hover:shadow-lg transition-all">
                    Request Emergency Service
                  </Button>
                </a>
              </motion.div>

              <motion.div variants={fadeInUp} className="mt-8 flex flex-col sm:flex-row gap-6 items-start sm:items-center">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">Open 7 Days a Week</p>
                    <p className="text-xs text-muted-foreground">Early 7 AM starts on weekdays</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-secondary" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">Licensed & Insured</p>
                    <p className="text-xs text-muted-foreground">Honest pricing guaranteed</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className="md:col-span-5"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative mt-8 md:mt-0">
                <img
                  src="https://images.pexels.com/photos/6471911/pexels-photo-6471911.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="HVAC Technician"
                  className="rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] w-full h-auto max-w-lg mx-auto md:max-w-none"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Bento Grid */}
      <section id="about" className="py-24 px-6 bg-white">
        <div className="container mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 font-chivo">
              Why Choose Us?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Located on Allen Blvd, we're proud to hold a flawless 5-star reputation in our Beaverton community.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-12 gap-6"
          >
            <motion.div
              variants={fadeInUp}
              className="md:col-span-8 bg-gradient-to-br from-secondary/5 to-secondary/10 rounded-2xl p-12 border border-slate-200"
            >
              <div className="flex items-start gap-6">
                <div className="bg-secondary text-white p-4 rounded-xl">
                  <Clock className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-4xl font-bold text-foreground mb-2 font-chivo">7 AM Start Times</h3>
                  <p className="text-lg text-muted-foreground mb-4">
                    We know HVAC emergencies don't wait for convenient hours. That's why our technicians start at 7 AM on weekdays and are available all weekend long.
                  </p>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">Monday - Friday: 7:00 AM - 8:00 PM</p>
                    <p className="text-sm font-medium text-foreground">Saturday - Sunday: 9:00 AM - 7:00 PM</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="md:col-span-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8 border border-slate-200 flex flex-col justify-center items-center text-center"
            >
              <div className="bg-primary text-white p-6 rounded-full mb-4">
                <Star className="h-12 w-12 fill-white" />
              </div>
              <h3 className="text-5xl font-black text-foreground mb-2 font-chivo">5.0</h3>
              <p className="text-sm text-muted-foreground">Perfect Rating</p>
              <p className="text-xs text-muted-foreground mt-2">Based on customer reviews</p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="md:col-span-4 rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-200"
            >
              <img
                src="https://images.unsplash.com/photo-1566908829550-e6551b00979b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3VzZSUyMGV4dGVyaW9yJTIwZGF5fGVufDB8fHx8MTc3NDM0NjI5NXww&ixlib=rb-4.1.0&q=85"
                alt="Modern home"
                className="w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="md:col-span-8 bg-card rounded-2xl p-12 border border-slate-200 hover:border-primary transition-colors"
            >
              <h3 className="text-2xl font-bold text-foreground mb-6 font-chivo">Full-Service HVAC Experts</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">Complex ductwork & furnace repairs</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">Energy-efficient AC upgrades</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">Routine maintenance & tune-ups</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">Complete system replacements</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">Honest pricing & diagnostics</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">Expert craftsmanship guaranteed</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 px-6 bg-background">
        <div className="container mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 font-chivo">
              Our Services
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive HVAC solutions for your home's comfort
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-card rounded-2xl p-8 border border-slate-200 hover:border-primary hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all hover:-translate-y-1"
                  data-testid={`service-card-${index}`}
                >
                  <div className="bg-primary/10 text-primary p-4 rounded-xl inline-flex mb-6">
                    <Icon className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3 font-chivo">{service.title}</h3>
                  <p className="text-sm text-muted-foreground">{service.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 px-6 bg-white">
        <div className="container mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 font-chivo">
              What Our Neighbors Say
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Don't just take our word for it - see what Beaverton residents have to say
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-card rounded-2xl p-8 border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                data-testid={`testimonial-card-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-secondary text-secondary" />
                  ))}
                </div>
                <p className="text-sm text-foreground mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-foreground text-sm">{testimonial.name}</p>
                  <p className="text-xs text-muted-foreground">{testimonial.location}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-background">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6 font-chivo">
                Get in Touch
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground mb-8">
                Need emergency HVAC service or have questions? We're here to help 7 days a week.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1">Phone</p>
                    <a href="tel:+19713665948" className="text-lg font-bold text-primary hover:underline">
                      (971) 366-5948
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1">Address</p>
                    <p className="text-muted-foreground">10200 SW Allen Blvd<br />Beaverton, OR 97005</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <Calendar className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1">Business Hours</p>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>Monday - Friday: 7:00 AM - 8:00 PM</p>
                      <p>Saturday - Sunday: 9:00 AM - 7:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <img
                  src="https://images.unsplash.com/photo-1584980770387-586712373e40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxodmFjJTIwdGVjaG5pY2lhbiUyMHdvcmtpbmd8ZW58MHx8fHwxNzc0MzQ2Mjk0fDA&ixlib=rb-4.1.0&q=85"
                  alt="Professional HVAC service"
                  className="rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] w-full"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card rounded-2xl p-8 border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            >
              <h3 className="text-2xl font-bold text-foreground mb-6 font-chivo">Request Service</h3>
              
              <form onSubmit={handleContactSubmit} data-testid="contact-form">
                <input type="hidden" name="access_key" value="YOUR_WEB3FORMS_ACCESS_KEY" />
                <input type="hidden" name="subject" value="New Service Request from Website" />
                <input type="checkbox" name="botcheck" style={{ display: 'none' }} />
                
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-foreground mb-2 block">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="John Smith"
                      className="w-full"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-foreground mb-2 block">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="(555) 123-4567"
                      className="w-full"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="service" className="text-sm font-medium text-foreground mb-2 block">Service Needed *</Label>
                    <Input
                      id="service"
                      name="service"
                      type="text"
                      required
                      placeholder="Furnace repair, AC installation, etc."
                      className="w-full"
                      data-testid="contact-service-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-sm font-medium text-foreground mb-2 block">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      placeholder="Tell us about your HVAC needs..."
                      className="w-full"
                      data-testid="contact-message-textarea"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-secondary hover:bg-secondary/90 text-white font-semibold py-6 rounded-lg hover:translate-y-[-2px] hover:shadow-lg transition-all"
                    data-testid="contact-submit-button"
                  >
                    Send Message
                  </Button>

                  <div id="contact-result"></div>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-6">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/6de07f33-8792-4205-9d68-0162561d985b/images/fb9d16bc83522b5dabea48add564fa81a7742b5e12468b7fae67c337b24374b6.png" 
                  alt="Logo" 
                  className="h-10 w-10"
                />
                <div>
                  <h3 className="text-lg font-bold font-chivo">Beaverton Heating & Cooling</h3>
                  <div className="flex gap-1 mt-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-3 w-3 fill-secondary text-secondary" />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-sm text-slate-400">
                Your trusted 5-star HVAC experts serving Beaverton and surrounding areas.
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-4 font-chivo">Contact Info</h4>
              <div className="space-y-3 text-sm text-slate-400">
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <a href="tel:+19713665948" className="hover:text-white transition-colors text-xl font-bold text-secondary">
                    (971) 366-5948
                  </a>
                </p>
                <p className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-1" />
                  <span>10200 SW Allen Blvd<br />Beaverton, OR 97005</span>
                </p>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4 font-chivo">Business Hours</h4>
              <div className="space-y-2 text-sm text-slate-400">
                <p>Monday - Friday: <span className="text-white font-semibold">7:00 AM - 8:00 PM</span></p>
                <p>Saturday - Sunday: <span className="text-white font-semibold">9:00 AM - 7:00 PM</span></p>
                <p className="text-secondary font-semibold mt-4">Available 7 Days a Week!</p>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-400">
            <p>&copy; {new Date().getFullYear()} Beaverton Heating and Cooling. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
